<script setup lang="ts">
import { IconAlertTriangle } from '@tabler/icons-vue';
import { onMounted, onBeforeUnmount } from 'vue';

import { useStore } from '@/store';

const emit = defineEmits(['confirm', 'cancel']);
const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    emit('confirm');
  }
};

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyDown);
});

const store = useStore();
const props = defineProps<{
  title: string;
  titleId?: string;
  message?: string;
  messageId?: string;
  confirmText?: string;
  cancelText?: string;
  confirmBtnDataTestId?: string;
  type?: string;
  customClass?: string;
}>();
</script>

<template>
  <div
    class="m-2 flex max-h-full min-w-[300px] max-w-md flex-col rounded-xl border border-primary-900 bg-primary-1000 shadow-2xl"
    :style="store?.state.event.goldcastThemeStyles"
  >
    <div class="relative flex-1 overflow-y-auto overflow-x-hidden px-4 py-6">
      <div class="flex items-start">
        <div
          v-if="props.type === 'warning'"
          :class="[
            'mr-4 flex h-10 w-10 min-w-10 items-center justify-center rounded-full bg-red-50',
            props.customClass
          ]"
        >
          <IconAlertTriangle aria-hidden="true" focusable="false" class="text-red-500" />
        </div>
        <div>
          <h5
            :id="props.titleId"
            :class="[
              'text-md false false font-medium leading-tight text-primaryText-100 lg:text-lg lg:leading-tight',
              props.message ? 'mb-1' : ''
            ]"
          >
            {{ $t(props.title) }}
          </h5>
          <p
            v-if="props.message"
            :id="props.messageId"
            class="text-sm text-primaryText-50"
            v-html="$t(props.message)"
          />
        </div>
      </div>
    </div>
    <div
      v-if="props.confirmText || props.cancelText"
      class="item-center flex justify-end space-x-2 border-t border-primary-900 p-2"
    >
      <button
        v-if="props.cancelText"
        class="audited-dialog-action btn-text btn-primary-ghost rounded-md"
        type="button"
        @click="$emit('cancel')"
      >
        {{ $t(props.cancelText) }}
      </button>
      <button
        v-if="props.confirmText"
        class="audited-dialog-action btn-text btn-primary-alt rounded-md"
        type="button"
        :data-testid="props.confirmBtnDataTestId"
        @click="$emit('confirm')"
      >
        {{ $t(props.confirmText) }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.audited-dialog-action:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}
</style>
